import type { CheckResult, InspectionStatus, NcrStatus, Severity } from '../api.js';

/**
 * The words and marks the quality screens use, in one place because the
 * register and its forms both need them — and because a status spelled two
 * ways is a status people stop trusting.
 *
 * Never colour alone: every status has a SYMBOL as well as a label.
 */

export const INSPECTION_LABELS: Readonly<Record<InspectionStatus, string>> = {
  requested: 'Requested',
  scheduled: 'Scheduled',
  in_progress: 'Being walked',
  passed: 'Passed',
  passed_with_observations: 'Passed, with observations',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

/**
 * Nothing more is asked of an inspection in one of these.
 *
 * The same list as `INSPECTION_CLOSED` in the service, and it has to be: this
 * side decides whether to OFFER the edit and that side decides whether to allow
 * it. An action that is always refused is worse than no action at all.
 */
export const INSPECTION_CLOSED: readonly InspectionStatus[] = [
  'passed',
  'passed_with_observations',
  'failed',
  'cancelled',
];

export const INSPECTION_SYMBOLS: Readonly<Record<InspectionStatus, string>> = {
  requested: '○',
  scheduled: '◐',
  in_progress: '◐',
  passed: '✓',
  passed_with_observations: '✓',
  failed: '✗',
  cancelled: '×',
};

export const NCR_LABELS: Readonly<Record<NcrStatus, string>> = {
  open: 'Open',
  corrective_action_assigned: 'Being put right',
  work_complete: 'Done — waiting to be checked',
  verified: 'Put right and checked',
  void: 'Void',
};

export const NCR_SYMBOLS: Readonly<Record<NcrStatus, string>> = {
  open: '○',
  corrective_action_assigned: '◐',
  work_complete: '⊙',
  verified: '✓',
  void: '×',
};

export const NCR_MOVES: Partial<Record<NcrStatus, string>> = {
  corrective_action_assigned: 'Assign the fix',
  work_complete: 'Mark it done',
  verified: 'Check and close',
  void: 'Void',
};

export const RESULT_LABELS: Readonly<Record<CheckResult, string>> = {
  pass: 'Pass',
  fail: 'Fail',
  na: 'N/A',
};

export const SEVERITY_LABELS: Readonly<Record<Severity, string>> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

export const MIN_TEXT = 10;
export const MIN_REASON = 20;
